<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BoardsAPI from 'dashboard/api/boards';
import KanbanColumn from './components/KanbanColumn.vue';
import DealModal from './components/DealModal.vue';
import BoardModal from './components/BoardModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useStore } from 'vuex';

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const route = useRoute();

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const boards = ref([]);
const currentBoard = ref(null);
const isLoading = ref(true);
const isDeletingBoard = ref(false);
const showDealModal = ref(false);
const selectedDeal = ref(null);
const selectedStageId = ref(null);
const showBoardForm = ref(false);
const boardFormMode = ref('create');
const showBoardDropdown = ref(false);
const isSavingBoard = ref(false);
const boardDeleteDialogRef = ref(null);
const searchQuery = ref('');
const assigneeFilter = ref('');
const statusFilter = ref('');

const currentBoardName = computed(() => currentBoard.value?.name || '');
const isEditingBoard = computed(() => boardFormMode.value === 'edit');
const currentBoardInboxLabel = computed(
  () => currentBoard.value?.inbox_name || t('KANBAN.BOARD.NO_INBOX')
);

const totalValue = computed(() => {
  if (!currentBoard.value?.stages) return 0;
  return currentBoard.value.stages.reduce(
    (sum, s) => sum + (s.total_value || 0),
    0
  );
});

const totalDeals = computed(() => {
  if (!currentBoard.value?.stages) return 0;
  return currentBoard.value.stages.reduce(
    (sum, stage) => sum + (stage.deals?.length || 0),
    0
  );
});

const boardAssignees = computed(() => {
  const assignees = new Map();
  (currentBoard.value?.stages || []).forEach(stage => {
    (stage.deals || []).forEach(deal => {
      if (deal.assignee?.id) {
        assignees.set(deal.assignee.id, deal.assignee);
      }
    });
  });
  return [...assignees.values()].sort((a, b) => a.name.localeCompare(b.name));
});

const conversationStatuses = computed(() => {
  const statuses = new Set();
  (currentBoard.value?.stages || []).forEach(stage => {
    (stage.deals || []).forEach(deal => {
      (deal.conversations || []).forEach(conversation => {
        if (conversation.status) statuses.add(conversation.status);
      });
    });
  });
  return [...statuses].sort();
});

const dealMatchesFilters = deal => {
  const search = searchQuery.value.trim().toLowerCase();
  const searchableText = [
    deal.name,
    deal.contact?.name,
    deal.contact?.email,
    deal.contact?.phone_number,
    deal.assignee?.name,
    ...(deal.conversations || []).flatMap(conversation => [
      conversation.display_id,
      conversation.id,
      conversation.inbox_name,
      ...(conversation.labels || []),
    ]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const matchesSearch = !search || searchableText.includes(search);
  const matchesAssignee =
    !assigneeFilter.value ||
    Number(deal.assignee?.id) === Number(assigneeFilter.value);
  const matchesStatus =
    !statusFilter.value ||
    (deal.conversations || []).some(
      conversation => conversation.status === statusFilter.value
    );

  return matchesSearch && matchesAssignee && matchesStatus;
};

const filteredStages = computed(() => {
  return (currentBoard.value?.stages || []).map(stage => {
    const deals = (stage.deals || []).filter(dealMatchesFilters);
    return {
      ...stage,
      deals,
      deals_count: deals.length,
      total_value: deals.reduce(
        (sum, deal) => sum + (deal.amount_cents || 0),
        0
      ),
    };
  });
});

const visibleTotalDeals = computed(() =>
  filteredStages.value.reduce((sum, stage) => sum + stage.deals.length, 0)
);

const visibleTotalValue = computed(() =>
  filteredStages.value.reduce((sum, stage) => sum + stage.total_value, 0)
);

const hasActiveFilters = computed(
  () => !!searchQuery.value || !!assigneeFilter.value || !!statusFilter.value
);

const clearFilters = () => {
  searchQuery.value = '';
  assigneeFilter.value = '';
  statusFilter.value = '';
};

const formatCurrency = cents =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format((cents || 0) / 100);

const resetBoardForm = () => {
  boardFormMode.value = 'create';
};

const closeBoardForm = () => {
  showBoardForm.value = false;
  resetBoardForm();
};

const openCreateBoardForm = () => {
  resetBoardForm();
  showBoardDropdown.value = false;
  showBoardForm.value = true;
};

const openEditBoardForm = () => {
  if (!currentBoard.value) return;

  boardFormMode.value = 'edit';
  showBoardDropdown.value = false;
  showBoardForm.value = true;
};

const findDealInBoard = (board, dealId) => {
  if (!board || !dealId) return null;
  const normalizedDealId = Number(dealId);
  return (
    (board.stages || [])
      .flatMap(stage => stage.deals || [])
      .find(deal => Number(deal.id) === normalizedDealId) || null
  );
};

const openRouteDealIfPresent = board => {
  const routeDealId = route.query.deal_id;
  if (!routeDealId || !board) return;

  const routeBoardId = route.query.board_id;
  if (routeBoardId && Number(routeBoardId) !== Number(board.id)) return;

  const deal = findDealInBoard(board, routeDealId);
  if (!deal) return;

  selectedDeal.value = deal;
  selectedStageId.value = deal.stage_id;
  showDealModal.value = true;
};

const selectBoard = async (boardId, options = {}) => {
  try {
    const { data } = await BoardsAPI.getBoard(boardId);
    currentBoard.value = data;
    showBoardDropdown.value = false;
    if (options.openRouteDeal) {
      openRouteDealIfPresent(data);
    }
  } catch {
    useAlert(t('KANBAN.ERRORS.LOAD_BOARD'));
  }
};

const fetchBoards = async () => {
  try {
    if (!inboxes.value.length) {
      store.dispatch('inboxes/get');
    }
    const { data } = await BoardsAPI.get();
    boards.value = data;
    if (data.length > 0 && !currentBoard.value) {
      const routeBoardId = route.query.board_id;
      const boardFromRoute = routeBoardId
        ? data.find(board => Number(board.id) === Number(routeBoardId))
        : null;
      await selectBoard(boardFromRoute?.id || data[0].id, {
        openRouteDeal: true,
      });
    }
  } catch {
    // No boards yet
  } finally {
    isLoading.value = false;
  }
};

const upsertBoard = async payload => {
  isSavingBoard.value = true;
  try {
    if (isEditingBoard.value && currentBoard.value) {
      const { data } = await BoardsAPI.update(currentBoard.value.id, {
        board: payload.board,
      });
      boards.value = boards.value.map(board =>
        board.id === data.id ? { ...board, ...data } : board
      );
      currentBoard.value = data;
      useAlert(t('KANBAN.BOARD.UPDATE_SUCCESS'));
    } else {
      const { data } = await BoardsAPI.create({
        board: payload.board,
      });
      boards.value.push(data);
      currentBoard.value = data;
    }

    closeBoardForm();
  } catch {
    useAlert(
      isEditingBoard.value
        ? t('KANBAN.BOARD.UPDATE_ERROR')
        : t('KANBAN.ERRORS.CREATE_BOARD')
    );
  } finally {
    isSavingBoard.value = false;
  }
};

const createStage = async payload => {
  if (!currentBoard.value) return;
  try {
    await BoardsAPI.createStage(currentBoard.value.id, payload);
    await selectBoard(currentBoard.value.id);
  } catch {
    useAlert(t('KANBAN.BOARD.STAGE_SAVE_ERROR'));
  }
};

const updateStage = async (stageId, payload) => {
  if (!currentBoard.value) return;
  try {
    await BoardsAPI.updateStage(currentBoard.value.id, stageId, payload);
    await selectBoard(currentBoard.value.id);
  } catch {
    useAlert(t('KANBAN.BOARD.STAGE_SAVE_ERROR'));
  }
};

const deleteStage = async stageId => {
  if (!currentBoard.value) return;
  try {
    await BoardsAPI.deleteStage(currentBoard.value.id, stageId);
    await selectBoard(currentBoard.value.id);
  } catch {
    useAlert(t('KANBAN.BOARD.STAGE_DELETE_ERROR'));
  }
};

const openDeleteBoardDialog = () => {
  boardDeleteDialogRef.value?.open();
};

const deleteCurrentBoard = async () => {
  if (!currentBoard.value) return;

  const deletedBoardId = currentBoard.value.id;
  isDeletingBoard.value = true;
  try {
    await BoardsAPI.delete(deletedBoardId);
    boards.value = boards.value.filter(board => board.id !== deletedBoardId);
    currentBoard.value = null;
    boardDeleteDialogRef.value?.close();
    useAlert(t('KANBAN.BOARD.DELETE_SUCCESS'));

    if (boards.value.length) {
      await selectBoard(boards.value[0].id);
    }
  } catch {
    useAlert(t('KANBAN.BOARD.DELETE_ERROR'));
  } finally {
    isDeletingBoard.value = false;
  }
};

const onDealDropped = async ({ dealId, stageId }) => {
  if (!currentBoard.value) return;
  try {
    await BoardsAPI.moveDeal(currentBoard.value.id, dealId, stageId, 0);
    await selectBoard(currentBoard.value.id);
  } catch {
    useAlert(t('KANBAN.ERRORS.MOVE_DEAL'));
  }
};

const onAddDeal = stageId => {
  selectedDeal.value = null;
  selectedStageId.value = stageId;
  showDealModal.value = true;
};

const onDealClick = deal => {
  selectedDeal.value = deal;
  selectedStageId.value = deal.stage_id;
  showDealModal.value = true;
};

const onDealSaved = async () => {
  showDealModal.value = false;
  if (currentBoard.value) {
    await selectBoard(currentBoard.value.id);
  }
};

onMounted(fetchBoards);

watch(
  () => [route.query.board_id, route.query.deal_id],
  async () => {
    if (!route.query.deal_id || !boards.value.length) return;

    const boardId = route.query.board_id || currentBoard.value?.id;
    if (!boardId) return;

    if (Number(currentBoard.value?.id) !== Number(boardId)) {
      await selectBoard(boardId, { openRouteDeal: true });
      return;
    }

    openRouteDealIfPresent(currentBoard.value);
  }
);
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <div
      class="flex flex-shrink-0 flex-col gap-3 border-b border-n-weak bg-n-solid-1 px-4 py-4 lg:flex-row lg:items-center lg:justify-between"
    >
      <div class="flex min-w-0 flex-1 flex-wrap items-center gap-3">
        <div
          v-if="boards.length"
          v-on-clickaway="() => (showBoardDropdown = false)"
          class="relative"
        >
          <button
            type="button"
            class="flex min-h-11 min-w-[240px] max-w-full items-center justify-between gap-3 rounded-lg border border-n-weak bg-transparent px-4 py-2.5 text-left text-sm font-semibold text-n-slate-12 transition-colors hover:border-n-strong hover:bg-n-brand/5"
            :aria-expanded="showBoardDropdown"
            @click="showBoardDropdown = !showBoardDropdown"
          >
            <span class="flex min-w-0 items-center gap-2">
              <span class="i-lucide-kanban size-4 text-n-brand" />
              <span class="truncate">{{
                currentBoardName || $t('KANBAN.TITLE')
              }}</span>
            </span>
            <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
          </button>

          <div
            v-if="showBoardDropdown"
            class="absolute left-0 top-full z-40 mt-2 w-[320px] overflow-hidden rounded-lg border border-n-weak bg-n-background shadow-lg"
          >
            <div
              class="border-b border-n-weak px-4 py-2.5 text-xs font-semibold uppercase text-n-slate-10"
            >
              {{ $t('KANBAN.BOARD.SELECT_BOARD') }}
            </div>
            <button
              v-for="board in boards"
              :key="board.id"
              type="button"
              class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-n-brand/5"
              :class="
                currentBoard?.id === board.id
                  ? 'text-n-brand'
                  : 'text-n-slate-12'
              "
              @click="selectBoard(board.id)"
            >
              <span class="min-w-0">
                <span class="block truncate text-sm font-semibold">{{
                  board.name
                }}</span>
                <span class="mt-1 block truncate text-xs text-n-slate-11">
                  {{ board.inbox_name || $t('KANBAN.BOARD.NO_INBOX') }}
                  {{ $t('KANBAN.SEPARATOR') }}
                  {{
                    $t('KANBAN.BOARD.DEALS_COUNT', {
                      count: board.deals_count || 0,
                    })
                  }}
                </span>
              </span>
              <span
                v-if="currentBoard?.id === board.id"
                class="i-lucide-check size-4"
              />
            </button>
            <button
              type="button"
              class="flex w-full items-center gap-2 border-t border-n-weak px-4 py-3 text-sm font-semibold text-n-brand transition-colors hover:bg-n-brand/5"
              @click="openCreateBoardForm"
            >
              <span class="i-lucide-plus size-4" />
              {{ $t('KANBAN.BOARD.CREATE_NEW') }}
            </button>
          </div>
        </div>

        <div v-else class="flex min-w-0 items-center gap-2">
          <span class="i-lucide-kanban size-5 text-n-brand" />
          <h1 class="text-base font-semibold text-n-slate-12">
            {{ $t('KANBAN.TITLE') }}
          </h1>
        </div>

        <div class="flex min-w-0 flex-wrap items-center gap-2">
          <span
            v-if="currentBoard"
            class="inline-flex items-center gap-1 rounded-md border border-n-weak bg-transparent px-2 py-1 text-xs font-medium text-n-slate-11"
          >
            <span class="i-lucide-inbox size-3" />
            {{ currentBoardInboxLabel }}
          </span>
          <span
            v-if="currentBoard"
            class="inline-flex items-center gap-1 rounded-md border border-n-weak bg-transparent px-2 py-1 text-xs font-medium text-n-slate-11"
          >
            <span class="i-lucide-layers-3 size-3" />
            {{
              $t('KANBAN.BOARD.STAGES_COUNT', {
                count: currentBoard.stages?.length || 0,
              })
            }}
          </span>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <div
          v-if="currentBoard"
          class="flex items-center divide-x divide-n-weak rounded-lg border border-n-weak bg-transparent text-sm"
        >
          <span class="px-3 py-2 text-n-slate-11">
            {{ hasActiveFilters ? visibleTotalDeals : totalDeals }}
          </span>
          <span class="px-3 py-2 font-semibold text-n-slate-12">
            {{
              formatCurrency(hasActiveFilters ? visibleTotalValue : totalValue)
            }}
          </span>
        </div>
        <Button
          v-if="currentBoard && isAdmin"
          v-tooltip.top="$t('KANBAN.BOARD.EDIT')"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-pencil"
          :aria-label="$t('KANBAN.BOARD.EDIT')"
          @click="openEditBoardForm"
        />
        <Button
          v-if="currentBoard && isAdmin"
          v-tooltip.top="$t('KANBAN.BOARD.DELETE')"
          variant="ghost"
          color="ruby"
          size="sm"
          icon="i-lucide-trash-2"
          :aria-label="$t('KANBAN.BOARD.DELETE')"
          @click="openDeleteBoardDialog"
        />
        <Button
          sm
          solid
          blue
          icon="i-lucide-plus"
          :label="$t('KANBAN.NEW_BOARD')"
          @click="openCreateBoardForm"
        />
      </div>
    </div>

    <div
      v-if="currentBoard"
      class="flex-shrink-0 border-b border-n-weak bg-n-solid-1 px-4 py-3"
    >
      <div
        class="flex w-full flex-col gap-2.5 rounded-lg border border-n-weak bg-transparent p-2 lg:flex-row lg:items-center"
      >
        <label
          class="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-md px-3 py-2.5 transition-colors focus-within:bg-n-brand/5"
        >
          <span
            class="i-lucide-search pointer-events-none size-4 flex-shrink-0 text-n-slate-10"
          />
          <input
            v-model="searchQuery"
            type="search"
            class="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10"
            :placeholder="$t('KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
          />
        </label>

        <div
          class="flex flex-col gap-3 sm:flex-row sm:items-center lg:flex-shrink-0"
        >
          <select
            v-model="assigneeFilter"
            class="min-h-11 w-full min-w-[220px] rounded-md border border-n-weak bg-transparent py-2.5 pl-4 pr-10 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5 lg:w-auto"
            :aria-label="$t('KANBAN.FILTERS.ASSIGNEE')"
          >
            <option value="">{{ $t('KANBAN.FILTERS.ALL_ASSIGNEES') }}</option>
            <option
              v-for="assignee in boardAssignees"
              :key="assignee.id"
              :value="assignee.id"
            >
              {{ assignee.name }}
            </option>
          </select>

          <select
            v-model="statusFilter"
            class="min-h-11 w-full min-w-[180px] rounded-md border border-n-weak bg-transparent py-2.5 pl-4 pr-10 text-sm text-n-slate-12 outline-none transition-colors focus:border-n-brand/60 focus:bg-n-brand/5 lg:w-auto"
            :aria-label="$t('KANBAN.FILTERS.STATUS')"
          >
            <option value="">{{ $t('KANBAN.FILTERS.ALL_STATUSES') }}</option>
            <option
              v-for="status in conversationStatuses"
              :key="status"
              :value="status"
            >
              {{ status }}
            </option>
          </select>

          <Button
            v-if="hasActiveFilters"
            type="button"
            sm
            ghost
            slate
            icon="i-lucide-x"
            :label="$t('KANBAN.FILTERS.CLEAR')"
            @click="clearFilters"
          />
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center text-sm text-n-slate-11"
    >
      {{ $t('KANBAN.LOADING') }}
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!boards.length"
      class="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center"
    >
      <span
        class="grid size-12 place-content-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-10"
      >
        <span class="i-lucide-kanban size-6" />
      </span>
      <p class="max-w-sm text-sm text-n-slate-11">
        {{ $t('KANBAN.EMPTY_STATE') }}
      </p>
      <Button
        solid
        blue
        icon="i-lucide-plus"
        :label="$t('KANBAN.CREATE_BOARD')"
        @click="openCreateBoardForm"
      />
    </div>

    <!-- Kanban board -->
    <div
      v-else-if="currentBoard"
      class="flex min-h-0 min-w-0 flex-1 gap-3 overflow-x-auto px-4 py-3"
    >
      <KanbanColumn
        v-for="stage in filteredStages"
        :key="stage.id"
        :stage="stage"
        @deal-click="onDealClick"
        @deal-dropped="onDealDropped"
        @add-deal="onAddDeal"
      />
    </div>

    <!-- Deal modal -->
    <DealModal
      v-if="showDealModal"
      :board-id="currentBoard?.id"
      :stage-id="selectedStageId"
      :deal="selectedDeal"
      @close="showDealModal = false"
      @saved="onDealSaved"
    />

    <BoardModal
      v-if="showBoardForm"
      :board="isEditingBoard ? currentBoard : null"
      :inboxes="inboxes"
      :is-saving="isSavingBoard"
      @close="closeBoardForm"
      @save-board="upsertBoard"
      @create-stage="createStage"
      @update-stage="updateStage"
      @delete-stage="deleteStage"
    />

    <Dialog
      ref="boardDeleteDialogRef"
      type="alert"
      :title="$t('KANBAN.BOARD.DELETE_TITLE')"
      :description="
        $t('KANBAN.BOARD.DELETE_DESCRIPTION', { name: currentBoardName })
      "
      :confirm-button-label="$t('KANBAN.BOARD.DELETE_CONFIRM')"
      :cancel-button-label="$t('KANBAN.CANCEL')"
      :is-loading="isDeletingBoard"
      @confirm="deleteCurrentBoard"
    />
  </div>
</template>
